import { Flex, View, Typography, tokens } from '@sapphire-react/core';
import { useRef, ReactNode, ChangeEventHandler, FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSapphireMedia } from '../../hooks/useSapphireMedia';

interface MoneySliderProps {
  initialAmount?: number;
  min?: number;
  max?: number;
  minLabel?: string;
  maxLabel?: string;
  amount: number;
  step?: number;
  name?: string;
  isDisabled?: boolean;
  handleSliderChange: ChangeEventHandler<HTMLInputElement>;
  children?: ReactNode;
}
interface SliderProps {
  percent?: number;
  keyboardFocused?: boolean;
}

export const MoneySlider: FC<MoneySliderProps> = ({
  min = 0,
  max = 0,
  minLabel = '',
  maxLabel = '',
  amount,
  name = 'money',
  step = 10000,
  isDisabled = false,
  handleSliderChange,
}) => {
  const sliderRef = useRef<HTMLInputElement>(null);
  const { isExtraSmall, isSmall, isMedium } = useSapphireMedia();
  const { t } = useTranslation();

  const percent = max > min ? ((amount - min) / (max - min)) * 100 : 100;

  return (
    <View position={'relative'}>
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <View width={'100%'}>
          <View>
            <Slider
              ref={sliderRef}
              type={'range'}
              min={min}
              max={max}
              value={amount}
              step={step}
              onChange={handleSliderChange}
              percent={percent}
              tabIndex={isDisabled ? -1 : 0}
              disabled={isDisabled}
              aria-label={`Select amount between ${min}€ and ${max}€`}
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={amount}
              aria-valuetext={`${t('results.donutChart.loanAmount.label')} ${amount}€`}
              data-test-id={`${name}-slider`}
            />
          </View>
          <View
            display={'flex'}
            justifyContent={'space-between'}
            marginTop={5}
            width={'100%'}
          >
            <Typography.Body
              size={isExtraSmall || isSmall || isMedium ? 'xs' : 'sm'}
            >
              {minLabel}
            </Typography.Body>
            <Typography.Body
              size={isExtraSmall || isSmall || isMedium ? 'xs' : 'sm'}
            >
              {maxLabel}
            </Typography.Body>
          </View>
        </View>
      </Flex>
    </View>
  );
};

const Slider = styled.input<SliderProps>`
  -webkit-appearance: none;
  appearance: none;
  width: 96%;
  height: 16px;
  background: transparent;
  opacity: ${(props) => (props.disabled ? tokens.opacity.disabled.value : 1)};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: ${tokens.color.backgroundProgressFill.value};
    cursor: pointer;
    border-radius: 50%;
    margin-top: -6px;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: ${tokens.color.backgroundProgressFill.value};
    cursor: pointer;
    border-radius: 50%;
    margin-top: -4px;
  }

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 4px;
    background: ${(props) =>
      `linear-gradient(to right, ${tokens.color.backgroundProgressFill.value} ${props.percent}%, ${tokens.color.backgroundProgressTrack.value} ${props.percent}%)`};
  }

  &::-moz-range-track {
    height: 8px;
    background: ${(props) =>
      `linear-gradient(to right, ${tokens.color.backgroundProgressFill.value} ${props.percent}%, ${tokens.color.backgroundProgressTrack.value} ${props.percent}%)`};
    border-radius: 4px;
  }
`;
